import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { confirmPublicPolicyAck, publicPolicyAckQuery } from '../api/easeedeskPolicyAcks'
import { Spinner } from '../ui/Spinner'
import './PolicyAckPage.css'

/**
 * Pagina din emailul de confirmare a unei politici IT (CyFun GV.PO-01.1).
 * O deschide un angajat al clientului, FARA cont: fara antet si fara sesiune
 * (`barePages.ts`), cu jetonul din adresa drept cheie. Textul vine curatat de
 * server (`SafeHtml`), exact cel trimis in runda.
 */
export function PolicyAckPage({ token }: { token: string }) {
  const client = useQueryClient()
  const page = useQuery(publicPolicyAckQuery(token))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)

  if (page.isPending) return <Spinner />
  if (page.isError) {
    return (
      <div className="policy-ack" style={{ maxWidth: 760, margin: '48px auto', padding: '0 16px' }}>
        <h2>This link is no longer valid.</h2>
        <p className="text-muted">A newer reminder may have replaced it. Ask your IT contact to send it again.</p>
      </div>
    )
  }
  const data = page.data

  const confirm = async () => {
    setBusy(true)
    setError(false)
    try {
      client.setQueryData(publicPolicyAckQuery(token).queryKey, await confirmPublicPolicyAck(token))
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="policy-ack" style={{ maxWidth: 760, margin: '32px auto', padding: '0 16px' }}>
      <p className="text-muted" style={{ marginBottom: 4 }}>
        IT policy · {data.company}
      </p>
      <h2 style={{ marginTop: 0 }}>{data.title}</h2>
      <p>Hello {data.name}, please read the policy below and confirm that you have read and understood it.</p>
      <div className="policy-ack-body" style={{ border: '1px solid #e2e6ea', borderRadius: 6, padding: 16, margin: '16px 0', overflowWrap: 'anywhere' }} dangerouslySetInnerHTML={{ __html: data.body }} />
      {data.confirmedAt ? (
        <div className="alert alert-success" role="status">
          <i className="fas fa-check-circle" aria-hidden="true"></i> Thank you. You confirmed this policy on {data.confirmedAt.slice(0, 16)}.
        </div>
      ) : (
        <>
          {error && <div className="alert alert-danger">The confirmation was not saved. Please try again.</div>}
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Saving...' : 'I have read and understood this policy'}
          </button>
        </>
      )}
    </div>
  )
}
